import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { AuthUser } from '../types'

/**
 * Auth store for client-side auth state.
 *
 * - Persisted to localStorage for session continuity across tabs
 * - DevTools enabled for debugging
 * - User data synced from Supabase auth events
 *
 * @example
 * ```tsx
 * const { user, isAuthenticated, setUser, setAuth, clearAuth } = useAuthStore()
 * ```
 */

interface AuthState {
	user: AuthUser | null
	isAuthenticated: boolean
	isLoading: boolean
	isHydrated: boolean
	onboardingCompleted: boolean
}

interface AuthActions {
	/** Set user data (preferred method) */
	setUser: (user: AuthUser | null) => void
	/** Alias for setUser - backwards compatibility with legacy store */
	setAuth: (user: AuthUser | null) => void
	setLoading: (loading: boolean) => void
	setHydrated: (hydrated: boolean) => void
	setOnboardingCompleted: (completed: boolean) => void
	/** Clear all auth state and logout */
	clearAuth: () => void
}

type AuthStore = AuthState & AuthActions

const initialState: AuthState = {
	user: null,
	isAuthenticated: false,
	isLoading: true,
	isHydrated: false,
	onboardingCompleted: false,
}

export const useAuthStore = create<AuthStore>()(
	devtools(
		persist(
			(set) => ({
				...initialState,

				setUser: (user) =>
					set(
						{
							user,
							isAuthenticated: !!user,
							isLoading: false,
						},
						false,
						'auth/setUser'
					),

				// Alias for backwards compatibility
				setAuth: (user) =>
					set(
						{
							user,
							isAuthenticated: !!user,
							isLoading: false,
						},
						false,
						'auth/setAuth'
					),

				setLoading: (isLoading) => set({ isLoading }, false, 'auth/setLoading'),

				setHydrated: (isHydrated) => set({ isHydrated }, false, 'auth/setHydrated'),

				setOnboardingCompleted: (onboardingCompleted) =>
					set({ onboardingCompleted }, false, 'auth/setOnboardingCompleted'),

				clearAuth: () =>
					set({ ...initialState, isLoading: false, isHydrated: true }, false, 'auth/clearAuth'),
			}),
			{
				name: 'auth-storage',
				partialize: (state) => ({
					user: state.user,
					isAuthenticated: state.isAuthenticated,
					onboardingCompleted: state.onboardingCompleted,
				}),
				onRehydrateStorage: () => (state) => {
					state?.setHydrated(true)
				},
			}
		),
		{ name: 'AuthStore' }
	)
)
